import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import dayjs from 'dayjs';

const SectorDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sector, setSector] = useState(null);
  const [flightLogs, setFlightLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [tlbEntries, setTlbEntries] = useState([]);
  const [tlbLoading, setTlbLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`/api/ops/sectors/${id}`);
        setSector(res.data);
      } finally {
        setLoading(false);
      }
    };
    if (id) load();
  }, [id]);

  useEffect(() => {
    const loadTlb = async () => {
      if (!id) return;
      try {
        setTlbLoading(true);
        // show both active + archived linked entries
        const res = await axios.get(`/api/technical-log-book/sector/${id}?includeArchived=true`);
        setTlbEntries(res.data || []);
      } catch {
        setTlbEntries([]);
      } finally {
        setTlbLoading(false);
      }
    };
    loadTlb();
  }, [id]);

  useEffect(() => {
    const loadLogs = async () => {
      if (!id) return;
      try {
        setLogsLoading(true);
        const res = await axios.get(`/api/crew/sectors/${id}/flight-log`);
        setFlightLogs(res.data || []);
      } catch {
        setFlightLogs([]);
      } finally {
        setLogsLoading(false);
      }
    };
    loadLogs();
  }, [id]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!sector) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography sx={{ mt: 2 }}>Sector not found.</Typography>
      </Box>
    );
  }

  const tripLabel = sector.tripId?.charterNumber || sector.tripId?.title || 'N/A';
  const aircraftLabel = sector.aircraftId?.registration
    ? `${sector.aircraftId.registration}${sector.aircraftId.model ? ` • ${sector.aircraftId.model}` : ''}`
    : 'N/A';

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)}>
          Back
        </Button>
        <Chip label={sector.status || 'N/A'} size="small" />
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Sector Details
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Trip: {tripLabel} | Sector: {sector.seqNo != null ? sector.seqNo : 'N/A'} | Aircraft: {aircraftLabel}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Departure</Typography>
            <Typography variant="body2">
              {sector.departure?.airport || 'N/A'}{' '}
              {sector.departure?.date ? `• ${dayjs(sector.departure.date).format('DD MMM YYYY')}` : ''}
              {sector.departure?.time ? ` ${sector.departure.time}` : ''}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Arrival</Typography>
            <Typography variant="body2">
              {sector.arrival?.airport || 'N/A'}{' '}
              {sector.arrival?.estimatedDate ? `• ${dayjs(sector.arrival.estimatedDate).format('DD MMM YYYY')}` : ''}
              {sector.arrival?.estimatedTime ? ` ${sector.arrival.estimatedTime}` : ''}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Assigned Pilots</Typography>
            <Typography variant="body2">
              {(sector.assignedPilots || []).length
                ? sector.assignedPilots.map((p) => `${p.firstName || ''} ${p.lastName || ''}`.trim()).join(', ')
                : 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Created</Typography>
            <Typography variant="body2">{sector.createdAt ? dayjs(sector.createdAt).format('DD MMM YYYY, HH:mm') : 'N/A'}</Typography>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Technical Log Entries
        </Typography>
        {logsLoading ? (
          <CircularProgress size={20} />
        ) : flightLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No Technical log entries found for this sector.
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {flightLogs.map((e) => (
              <li key={e._id}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ py: 0.5 }}>
                  <Chip label={e.entryType} size="small" />
                  <Typography variant="body2">
                    {e.createdAt ? dayjs(e.createdAt).format('DD MMM YYYY, HH:mm') : e._id}
                  </Typography>
                  <Button size="small" onClick={() => navigate(`/flight-log/${e._id}`)}>
                    Open
                  </Button>
                </Box>
              </li>
            ))}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom>
          Technical Log Book Entries
        </Typography>
        {tlbLoading ? (
          <CircularProgress size={20} />
        ) : tlbEntries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No Technical Log Book entries linked to this sector.
          </Typography>
        ) : (
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {tlbEntries.map((t) => (
              <li key={t._id}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ py: 0.5 }}>
                  <Chip label={t.workflowStatus || 'N/A'} size="small" />
                  {t.isActive === false && <Chip label="ARCHIVED" size="small" variant="outlined" color="warning" />}
                  <Typography variant="body2">
                    Sheet: {t.sheetNumber || 'Not set'} • Aircraft: {t.aircraftId?.registration || 'N/A'}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => navigate('/technical-log-book', { state: { openEntryId: t._id } })}
                  >
                    Open
                  </Button>
                </Box>
              </li>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default SectorDetailPage;

